import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatKES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Star, MapPin, BadgeCheck, Lock, MessageCircle, Calendar, ShieldCheck, ArrowLeft, Reply } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function EmployeeProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [hireOpen, setHireOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const [hire, setHire] = useState({ start_date: "", duration: "monthly", description: "", offered_rate: 0 });
  const [msg, setMsg] = useState("");
  const [review, setReview] = useState({ rating: 5, comment: "" });

  const load = () => api.get(`/employees/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="p-20 text-center text-[#5B6478]">Loading...</div>;
  const emp = data.employee;

  const submitHire = async () => {
    if (!user) return nav("/login");
    try {
      const suggested = emp.industry_rate?.daily_max || 0;
      await api.post("/hires", {
        employee_id: emp.id,
        start_date: hire.start_date,
        duration: hire.duration,
        description: hire.description,
        offered_rate: Number(hire.offered_rate) || suggested,
      });
      toast.success("Match request sent. KSB will contact you shortly to confirm placement.");
      setHireOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const startThread = async () => {
    if (!user) return nav("/login");
    try {
      // We don't expose user_id on emp publicly. Use a "start-thread-by-employee" approach via tickets-like flow.
      // Simpler: use ticket endpoint for anonymous chat initiation.
      await api.post("/tickets", { employee_id: emp.id, subject: "Inquiry via profile", message: msg });
      toast.success("Inquiry sent. KSB will route to the staff member.");
      setMsgOpen(false);
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitReview = async () => {
    if (!user) return nav("/login");
    try {
      await api.post("/reviews", { employee_id: emp.id, rating: Number(review.rating), comment: review.comment });
      toast.success("Review posted");
      setReviewOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <button onClick={() => nav(-1)} data-testid="profile-back" className="flex items-center gap-2 text-sm text-[#5B6478] hover:text-[#1E3A8A]">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mt-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="aspect-square bg-[#EEF0FA] rounded-md overflow-hidden">
              {emp.photo_url && <img src={emp.photo_url} alt={emp.full_name} className="w-full h-full object-cover" />}
            </div>
            <div className="mt-5 flex items-center gap-2">
              <div className="flex items-center gap-1 bg-[#EEF0FA] px-3 py-1.5 rounded-md">
                <Star className="w-4 h-4 fill-[#7C3AED] text-[#7C3AED]" />
                <span className="font-semibold">{emp.rating_avg?.toFixed(1) || "—"}</span>
                <span className="text-xs text-[#5B6478]">· {emp.rating_count || 0} reviews</span>
              </div>
              {emp.verified && (
                <div className="flex items-center gap-1 bg-[#1E3A8A] text-[#F8F9FE] px-3 py-1.5 rounded-md text-xs">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-2 capitalize">{emp.category.replace("-", " ")}</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">{emp.full_name}</h1>
            <div className="flex items-center gap-4 mt-3 text-sm text-[#5B6478]">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {emp.location}</span>
              <span>· {emp.experience_years} yrs experience</span>
            </div>

            <div className="bg-[#EEF0FA] border border-[#E4E7F5] rounded-md p-5 mt-6 flex items-center gap-3">
              <Lock className="w-5 h-5 text-[#1E3A8A]" />
              <div className="text-sm">
                <div className="font-semibold">Contact details are private</div>
                <div className="text-[#5B6478]">Communicate through KSB messaging or send a hire request.</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow mb-2">About</div>
              <p className="leading-relaxed text-[#0F1E4B]">{emp.bio}</p>
            </div>

            <div className="mt-6">
              <div className="label-eyebrow mb-3">Skills</div>
              <div className="flex flex-wrap gap-2">
                {emp.skills?.map((s) => (
                  <span key={s} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-[#E4E7F5] rounded-md bg-white">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 bg-white border border-[#E4E7F5] rounded-md p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="label-eyebrow flex items-center gap-1"><Lock className="w-3 h-3" /> Industry rate band</div>
                  {emp.industry_rate ? (
                    <>
                      <div className="font-display text-3xl font-bold text-[#1E3A8A] mt-1">
                        {formatKES(emp.industry_rate.daily_min)} – {formatKES(emp.industry_rate.daily_max)}
                        <span className="text-base text-[#5B6478] font-normal">/day</span>
                      </div>
                      <div className="text-xs text-[#5B6478] mt-1">
                        Monthly: {formatKES(emp.industry_rate.monthly_min)} – {formatKES(emp.industry_rate.monthly_max)}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-[#5B6478] mt-1">Contact the bureau for a quote</div>
                  )}
                  <div className="text-xs text-[#5B6478] mt-2 max-w-md">
                    The exact wage is determined by KSB based on the staff member's qualifications and matched to your contractual agreement.
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    data-testid="profile-message-btn"
                    variant="outline"
                    className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-[#F8F9FE]"
                    onClick={() => {
                      if (!user) {
                        toast.info("Create an employer account to message staff");
                        return nav(`/signup?role=customer&next=/employees/${emp.id}`);
                      }
                      setMsgOpen(true);
                    }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" /> Message
                  </Button>
                  <Button
                    data-testid="profile-hire-btn"
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                    onClick={() => {
                      if (!user) {
                        toast.info("Create an employer account to request a match");
                        return nav(`/signup?role=customer&next=/employees/${emp.id}`);
                      }
                      setHireOpen(true);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" /> Request Match
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-16">
          <div className="flex items-end justify-between">
            <div>
              <div className="label-eyebrow mb-2">Reviews</div>
              <h2 className="font-display text-3xl font-bold">{data.reviews.length} customer review{data.reviews.length !== 1 && "s"}</h2>
            </div>
            {user?.role === "customer" && (
              <Button data-testid="add-review-btn" onClick={() => setReviewOpen(true)} variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-[#F8F9FE]">
                Leave a review
              </Button>
            )}
          </div>
          <div className="mt-6 space-y-4">
            {data.reviews.length === 0 && <div className="text-[#5B6478]">No reviews yet.</div>}
            {data.reviews.map((r) => (
              <div key={r.id} className="bg-white border border-[#E4E7F5] rounded-md p-5">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold">{r.customer_name}</div>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-[#7C3AED] text-[#7C3AED]" : "text-[#E4E7F5]"}`} />
                    ))}
                  </div>
                </div>
                <p className="text-[#5B6478] mt-2 leading-relaxed">{r.comment}</p>
                {r.replies?.map((rp) => (
                  <div key={rp.id} className="mt-3 ml-4 pl-4 border-l-2 border-[#7C3AED] bg-[#EEF0FA] rounded-r-md p-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-display font-bold uppercase tracking-widest text-[#7C3AED]">
                        {rp.author_role === "admin" ? "KSB · " : "Staff · "}{rp.author_name}
                      </span>
                      <span className="text-[#5B6478]">· {new Date(rp.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-sm mt-1">{rp.body}</div>
                  </div>
                ))}
                {(user?.role === "employee" || user?.role === "admin") && (
                  <div className="mt-3">
                    <ReviewReplyForm reviewId={r.id} onReplied={load} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />

      {/* HIRE DIALOG */}
      <Dialog open={hireOpen} onOpenChange={setHireOpen}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Request to match with {emp.full_name}</DialogTitle>
            <DialogDescription>KSB will review your request, negotiate a fair wage based on the staff member's qualifications, and draft a signed contract before placement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="label-eyebrow">Preferred start date</Label>
                <Input data-testid="hire-start-date" type="date" value={hire.start_date} onChange={(e) => setHire({ ...hire, start_date: e.target.value })} className="mt-2 border-[#E4E7F5]" />
              </div>
              <div>
                <Label className="label-eyebrow">Duration</Label>
                <select
                  data-testid="hire-duration"
                  value={hire.duration}
                  onChange={(e) => setHire({ ...hire, duration: e.target.value })}
                  className="mt-2 w-full h-10 border border-[#E4E7F5] rounded-md px-3 bg-white"
                >
                  <option value="one_time">One time job</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="permanent">Permanent</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Your budget (KES / day, optional)</Label>
              <Input
                data-testid="hire-rate"
                type="number"
                value={hire.offered_rate}
                onChange={(e) => setHire({ ...hire, offered_rate: e.target.value })}
                className="mt-2 border-[#E4E7F5]"
                placeholder={emp.industry_rate ? `Industry: ${emp.industry_rate.daily_min} – ${emp.industry_rate.daily_max}` : ""}
              />
            </div>
            <div>
              <Label className="label-eyebrow">Describe the job</Label>
              <Textarea data-testid="hire-description" rows={3} value={hire.description} onChange={(e) => setHire({ ...hire, description: e.target.value })} className="mt-2 border-[#E4E7F5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHireOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="hire-submit" onClick={submitHire} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Send Match Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MESSAGE DIALOG */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Send an inquiry</DialogTitle>
            <DialogDescription>Your contact details stay private. KSB will route the message to the staff member.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="msg-body" rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Hi, I'd like to discuss..." className="border-[#E4E7F5]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="msg-submit" disabled={!msg} onClick={startThread} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVIEW DIALOG */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-[#F8F9FE]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Leave a review</DialogTitle>
            <DialogDescription>You can only review staff you've hired.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-eyebrow">Rating</Label>
              <div className="mt-2 flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} data-testid={`star-${n}`} onClick={() => setReview({ ...review, rating: n })} type="button">
                    <Star className={`w-7 h-7 ${n <= review.rating ? "fill-[#7C3AED] text-[#7C3AED]" : "text-[#E4E7F5]"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Comment</Label>
              <Textarea data-testid="review-comment" rows={4} value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} className="mt-2 border-[#E4E7F5]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} className="border-[#E4E7F5]">Cancel</Button>
            <Button data-testid="review-submit" onClick={submitReview} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]">Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewReplyForm({ reviewId, onReplied }) {
  const [open, setOpen] = React.useState(false);
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/reviews/${reviewId}/reply`, { body: body.trim() });
      toast.success("Reply posted");
      setBody("");
      setOpen(false);
      onReplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reply");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={`reply-${reviewId}`}
        onClick={() => setOpen(true)}
        className="text-xs font-semibold uppercase tracking-widest text-[#7C3AED] hover:text-[#1E3A8A] flex items-center gap-1"
      >
        <Reply className="w-3 h-3" /> Reply
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <Textarea
        data-testid={`reply-body-${reviewId}`}
        rows={2}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Reply as staff or KSB..."
        className="border-[#E4E7F5]"
      />
      <div className="flex flex-col gap-1">
        <Button data-testid={`reply-submit-${reviewId}`} size="sm" disabled={busy || !body.trim()} onClick={submit} className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-white">Post</Button>
        <Button size="sm" variant="outline" onClick={() => { setOpen(false); setBody(""); }} className="border-[#E4E7F5]">Cancel</Button>
      </div>
    </div>
  );
}
